// Main content area — switches between view modes:
//   compare → side-by-side OLD | NEW
//   slider  → single image with drag slider revealing OLD over NEW
//   overlay → OLD on top with opacity + mix-blend (difference) over NEW
//   diff    → server-rendered diff image (Phase 10E backend add-on)
//   history → version selector + single image with history annotations
//   last    → NEW only
//
// Render is gated on activeProjectId + activeTargetId. Empty states reuse
// the legacy `.empty-state` markup. RectOverlays inside each capture cell
// are filtered strictly per (targetId, resolutionName, captureId) — Bug 2 fix.

import type { Remark } from '@ladd-screenshot/shared';
import { For, Show, createEffect, createMemo, createResource, onCleanup } from 'solid-js';
import { api } from '../api/client';
import { actions, stores } from '../state/store';
import type { CaptureRow } from '../state/store';
import { LoadingState } from './LoadingState';
import { RemarkFormModal } from './RemarkFormModal';

// Compute the effective scale to apply to .screenshot-inner. Mirrors the
// legacy `applyZoom()` strategy (index.html L4376-4395): for fit modes we
// derive a numeric scale from the panel viewport vs. the image natural size,
// then size `.screenshot-inner` explicitly in pixels and stretch the <img>
// to 100% of the inner. Plain CSS `height: 100%` does not work because the
// inline-block inner has no fixed height to resolve against.
function computeScale(panel: HTMLElement, img: HTMLImageElement): number {
  if (!img.naturalWidth || !img.naturalHeight) return 1;
  const mode = stores.state.zoomFitMode;
  if (mode === 'fitWidth') return (panel.clientWidth - 16) / img.naturalWidth;
  if (mode === 'fitHeight') return (panel.clientHeight - 16) / img.naturalHeight;
  if (mode === 'auto') return Math.min(1, (panel.clientWidth - 16) / img.naturalWidth);
  return stores.state.zoomPercent / 100;
}

function variantCaptures(variant: 'old' | 'new'): CaptureRow | null {
  const res = stores.state.activeResolution;
  if (!res) return null;
  return (
    stores.state.captures.find(
      (c) => c.variantId === variant && c.resolutionName === res,
    ) ?? null
  );
}

function visibleRemarksFor(capture: CaptureRow): Remark[] {
  // Bug 2 fix: rect overlays must be strictly scoped to the capture being
  // rendered. The legacy KO was that an unfiltered store leaked remarks
  // from other targets onto the footer view. Tightening the filter to
  // (variantId, resolutionName, captureId) closes that.
  if (!stores.state.remarksVisible) return [];
  return stores.state.remarks.filter((r) => {
    const sameVariant = r.variantId === capture.variantId;
    const sameRes = r.resolutionName === capture.resolutionName;
    const sameCaptureOrPending =
      r.captureId === capture.id || r.captureId == null;
    const hasRect =
      r.rect && typeof (r.rect as { x?: number }).x === 'number';
    const sameTarget =
      r.targetSlug === (stores.state.targets.find((t) => t.id === stores.state.activeTargetId)?.slug ?? '') ||
      // fall back: targetId equality if the slug isn't denormalised on this row
      r.targetSlug == null;
    return sameVariant && sameRes && sameCaptureOrPending && hasRect && sameTarget;
  });
}

interface CaptureCellProps {
  capture: CaptureRow;
  side: 'old' | 'new' | 'single';
}

function CaptureCell(props: CaptureCellProps) {
  const [src] = createResource(
    () => props.capture.id,
    (id) => api.fetchCaptureImageObjectUrl(id),
  );
  onCleanup(() => {
    const s = src();
    if (s) URL.revokeObjectURL(s);
  });

  const remarks = createMemo(() => visibleRemarksFor(props.capture));
  let frameRef: HTMLDivElement | undefined;
  let innerRef: HTMLDivElement | undefined;
  let imgRef: HTMLImageElement | undefined;
  let layerRef: HTMLDivElement | undefined;
  let currentScale = 1;
  let dragStart: { x: number; y: number } | null = null;
  let dragRectEl: HTMLDivElement | null = null;

  function applyZoom() {
    if (!frameRef || !innerRef || !imgRef) return;
    if (!imgRef.naturalWidth || !imgRef.naturalHeight) return;
    const scale = computeScale(frameRef, imgRef);
    currentScale = scale;
    innerRef.style.width = imgRef.naturalWidth * scale + 'px';
    innerRef.style.height = imgRef.naturalHeight * scale + 'px';
    innerRef.style.transform = '';
    imgRef.style.width = '100%';
    imgRef.style.height = '100%';
    // Annotation layer stays at natural size and scales via transform — same
    // strategy as legacy applyZoom() (index.html L4386-4391). Remark rects
    // inside use natural image coords (rect.x/y/w/h as stored), and the
    // transform aligns them visually with the scaled image.
    if (layerRef) {
      layerRef.style.width = imgRef.naturalWidth + 'px';
      layerRef.style.height = imgRef.naturalHeight + 'px';
      layerRef.style.transform = `scale(${scale})`;
    }
  }

  createEffect(() => {
    // Track reactive deps: fit mode, zoom percent, active resolution, current src.
    void stores.state.zoomFitMode;
    void stores.state.zoomPercent;
    void stores.state.activeResolution;
    void src();
    queueMicrotask(applyZoom);
  });

  function onImgLoad() {
    applyZoom();
  }

  if (typeof window !== 'undefined') {
    const onResize = () => applyZoom();
    window.addEventListener('resize', onResize);
    onCleanup(() => window.removeEventListener('resize', onResize));
  }

  // Drag-create logic. Coordinates are computed in NATURAL image space (i.e.
  // divided by currentScale) so the persisted rect lines up with the image
  // regardless of the active zoom — same approach as legacy startDrawing()
  // (index.html L3295-3353). The transient drawing-rect lives inside the
  // annotation-layer (which is transform-scaled), so its own dims stay in
  // natural coords too — no extra math when drawing.
  function naturalFromEvent(e: MouseEvent): { x: number; y: number } | null {
    if (!innerRef) return null;
    const rect = innerRef.getBoundingClientRect();
    const scale = currentScale || 1;
    return {
      x: (e.clientX - rect.left) / scale,
      y: (e.clientY - rect.top) / scale,
    };
  }

  function onMouseDown(e: MouseEvent) {
    if (!stores.state.annotateMode || !layerRef) return;
    const pt = naturalFromEvent(e);
    if (!pt) return;
    dragStart = pt;
    dragRectEl = document.createElement('div');
    dragRectEl.className = 'drawing-rect';
    dragRectEl.style.left = `${dragStart.x}px`;
    dragRectEl.style.top = `${dragStart.y}px`;
    dragRectEl.style.width = '0px';
    dragRectEl.style.height = '0px';
    layerRef.appendChild(dragRectEl);
    e.preventDefault();
  }
  function onMouseMove(e: MouseEvent) {
    if (!dragStart || !dragRectEl) return;
    const pt = naturalFromEvent(e);
    if (!pt) return;
    const left = Math.min(dragStart.x, pt.x);
    const top = Math.min(dragStart.y, pt.y);
    const w = Math.abs(pt.x - dragStart.x);
    const h = Math.abs(pt.y - dragStart.y);
    dragRectEl.style.left = `${left}px`;
    dragRectEl.style.top = `${top}px`;
    dragRectEl.style.width = `${w}px`;
    dragRectEl.style.height = `${h}px`;
  }
  function onMouseUp(e: MouseEvent) {
    if (!dragStart || !dragRectEl) return;
    const pt = naturalFromEvent(e);
    if (!pt) return;
    const left = Math.min(dragStart.x, pt.x);
    const top = Math.min(dragStart.y, pt.y);
    const w = Math.abs(pt.x - dragStart.x);
    const h = Math.abs(pt.y - dragStart.y);
    dragRectEl.remove();
    dragRectEl = null;
    dragStart = null;
    if (w < 10 || h < 10) return; // ignore micro-drags
    actions.setPendingRect({
      x: Math.round(left),
      y: Math.round(top),
      width: Math.round(w),
      height: Math.round(h),
      captureId: props.capture.id,
      variantId: props.capture.variantId,
      resolutionName: props.capture.resolutionName,
    });
  }

  // Panel URL row: legacy stbarts shows the source URL (production /
  // dev frontend) below the panel-header. Build from capture.metadata.url.
  const panelUrl = () =>
    (props.capture.metadata as { url?: string } | null)?.url ?? '';

  return (
    <div
      class={`compare-panel ${props.side === 'single' ? 'single-view' : ''}`}
      data-variant={props.capture.variantId}
    >
      <Show when={panelUrl()}>
        <div class="panel-url">{panelUrl()}</div>
      </Show>
      <div
        class={`screenshot ${stores.state.annotateMode ? 'drawing' : ''}`}
        ref={frameRef!}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
      >
        <Show when={src()} fallback={<div class="no-image">loading…</div>}>
          <div class="screenshot-inner" ref={innerRef!} style={{ 'transform-origin': 'top left' }}>
            <img
              ref={imgRef!}
              src={src() ?? ''}
              alt={`${props.capture.variantId} ${props.capture.resolutionName}`}
              onLoad={onImgLoad}
            />
            <div class="annotation-layer" ref={layerRef!}>
              <For each={remarks()}>
                {(remark, idx) => {
                  const r = remark.rect as { x: number; y: number; width: number; height: number };
                  return (
                    <div
                      class={`remark-rect ${remark.status}`}
                      style={{
                        left: `${r.x}px`,
                        top: `${r.y}px`,
                        width: `${r.width}px`,
                        height: `${r.height}px`,
                      }}
                      title={remark.text}
                      onClick={(e) => {
                        e.stopPropagation();
                        actions.openRemarkDetail(remark.id);
                      }}
                    >
                      <span class="remark-number">{idx() + 1}</span>
                    </div>
                  );
                }}
              </For>
            </div>
          </div>
        </Show>
      </div>
    </div>
  );
}

export function ContentView() {
  const oldCap = createMemo(() => variantCaptures('old'));
  const newCap = createMemo(() => variantCaptures('new'));

  // Synchronized scrolling between OLD and NEW panels in compare mode.
  // Mirrors legacy stbarts (index.html L4255-4281): percentage-based so the
  // two panels stay aligned even when their content is scaled differently.
  // Re-attached whenever viewMode / target / captures change so refs stay
  // fresh after Solid re-renders the compare-view subtree.
  createEffect(() => {
    void stores.state.viewMode;
    void stores.state.activeTargetId;
    void stores.state.activeResolution;
    void stores.state.zoomFitMode;
    void stores.state.zoomPercent;
    if (stores.state.viewMode !== 'compare') return;

    // Defer to next frame so DOM panels exist after the Show-block mounts.
    let detach: (() => void) | null = null;
    const raf = requestAnimationFrame(() => {
      const panels = document.querySelectorAll<HTMLDivElement>(
        '.compare-view .compare-panel .screenshot',
      );
      if (panels.length !== 2) return;
      const [panelOld, panelNew] = [panels[0]!, panels[1]!];
      let isSyncing = false;
      function sync(source: HTMLDivElement, target: HTMLDivElement) {
        if (isSyncing) return;
        isSyncing = true;
        const maxL = source.scrollWidth - source.clientWidth;
        const maxT = source.scrollHeight - source.clientHeight;
        const pctL = maxL > 0 ? source.scrollLeft / maxL : 0;
        const pctT = maxT > 0 ? source.scrollTop / maxT : 0;
        const tMaxL = target.scrollWidth - target.clientWidth;
        const tMaxT = target.scrollHeight - target.clientHeight;
        target.scrollLeft = pctL * tMaxL;
        target.scrollTop = pctT * tMaxT;
        requestAnimationFrame(() => { isSyncing = false; });
      }
      const onOld = () => sync(panelOld, panelNew);
      const onNew = () => sync(panelNew, panelOld);
      panelOld.addEventListener('scroll', onOld);
      panelNew.addEventListener('scroll', onNew);
      detach = () => {
        panelOld.removeEventListener('scroll', onOld);
        panelNew.removeEventListener('scroll', onNew);
      };
    });

    onCleanup(() => {
      cancelAnimationFrame(raf);
      detach?.();
    });
  });

  // OLD column header label, driven by leftVersion (legacy renderContent
  // L2967-2972). Alpha currently has only the 'old' baseline (no history
  // versions yet), so the label is either "History" (default placeholder)
  // or "Old (Production)" when 'old' is selected.
  const headerLeft = () => {
    if (stores.state.leftVersion === 'old') return 'Old (Production)';
    return 'History';
  };
  const headerRight = () => 'LAST';

  // Left panel shows content only when a version is actually picked.
  // leftVersion === 'current' means "no selection yet" → render the legacy
  // placeholder "Select a version to compare" (index.html L2995).
  const showOldPanel = () => stores.state.leftVersion !== 'current';

  // Show comparison-headers only when the view mode actually displays 2
  // panels (compare). Single-view modes (last / history) show 1 panel,
  // so the OLD column header is hidden and only the NEW label is shown.
  const showSplit = () =>
    stores.state.viewMode === 'compare' ||
    stores.state.viewMode === 'slider' ||
    stores.state.viewMode === 'overlay' ||
    stores.state.viewMode === 'diff';

  return (
    <div class="content" id="content">
      <Show
        when={stores.state.activeProjectId}
        fallback={
          <div class="empty-state">
            <div class="icon">📁</div>
            <p>Select a project from the sidebar</p>
          </div>
        }
      >
        <Show
          when={stores.state.activeTargetId}
          fallback={
            <div class="empty-state">
              <div class="icon">📸</div>
              <p>Select a target from the sidebar</p>
            </div>
          }
        >
          <Show
            when={!stores.state.capturesLoading || stores.state.captures.length > 0}
            fallback={<LoadingState variant="grid" />}
          >
          <Show
            when={stores.state.captures.length > 0}
            fallback={
              <div class="empty-state">
                <div class="icon">📷</div>
                <p>No captures yet for this target</p>
              </div>
            }
          >
            <div class="comparison-headers">
              <Show when={showSplit()}>
                <div class="panel-header old">
                  <span>{headerLeft()}</span>
                  <Show when={oldCap()}>
                    <span class="version-selector">
                      <select
                        id="versionSelect"
                        value={stores.state.leftVersion}
                        onChange={(e) =>
                          actions.setLeftVersion(
                            (e.currentTarget as HTMLSelectElement).value,
                          )
                        }
                      >
                        <option value="current">- Select version -</option>
                        <option value="old">Old (Production)</option>
                      </select>
                    </span>
                  </Show>
                </div>
              </Show>
              <div class="panel-header new">{headerRight()}</div>
            </div>

            <Show when={stores.state.viewMode === 'compare'}>
              <div class="compare-view">
                <Show
                  when={showOldPanel() && oldCap()}
                  fallback={
                    <div class="compare-panel">
                      <div class="no-image">
                        {oldCap()
                          ? 'Select a version to compare'
                          : 'No version available'}
                      </div>
                    </div>
                  }
                >
                  <CaptureCell capture={oldCap()!} side="old" />
                </Show>
                <Show
                  when={newCap()}
                  fallback={<div class="compare-panel"><div class="no-image">NEW capture missing</div></div>}
                >
                  <CaptureCell capture={newCap()!} side="new" />
                </Show>
              </div>
            </Show>

            <Show when={stores.state.viewMode === 'last'}>
              <Show
                when={newCap()}
                fallback={<div class="no-image">NEW capture missing</div>}
              >
                <CaptureCell capture={newCap()!} side="single" />
              </Show>
            </Show>

            <Show when={stores.state.viewMode === 'history'}>
              <Show
                when={oldCap()}
                fallback={<div class="no-image">No history yet</div>}
              >
                <CaptureCell capture={oldCap()!} side="single" />
              </Show>
            </Show>

            <Show when={stores.state.viewMode === 'slider' || stores.state.viewMode === 'overlay'}>
              <SliderOverlayView old={oldCap()} new={newCap()} mode={stores.state.viewMode as 'slider' | 'overlay'} />
            </Show>

            <Show when={stores.state.viewMode === 'diff'}>
              <DiffView old={oldCap()} new={newCap()} />
            </Show>
          </Show>
          </Show>
        </Show>
      </Show>

      <RemarkFormModal />
    </div>
  );
}

function SliderOverlayView(props: { old: CaptureRow | null; new: CaptureRow | null; mode: 'slider' | 'overlay' }) {
  const [oldSrc] = createResource(
    () => props.old?.id ?? null,
    async (id) => (id ? await api.fetchCaptureImageObjectUrl(id) : null),
  );
  const [newSrc] = createResource(
    () => props.new?.id ?? null,
    async (id) => (id ? await api.fetchCaptureImageObjectUrl(id) : null),
  );
  onCleanup(() => {
    const a = oldSrc();
    const b = newSrc();
    if (a) URL.revokeObjectURL(a);
    if (b) URL.revokeObjectURL(b);
  });

  return (
    <Show
      when={oldSrc() && newSrc()}
      fallback={<div class="no-image">Captures incomplètes pour ce mode</div>}
    >
      <Show when={props.mode === 'slider'}>
        <div class="slider-compare">
          <div class="slider-inner">
            <img class="slider-img new" src={newSrc() ?? ''} alt="new" />
            <img class="slider-img old" src={oldSrc() ?? ''} alt="old" />
            <div class="slider-handle" />
          </div>
        </div>
      </Show>
      <Show when={props.mode === 'overlay'}>
        <div class="overlay-compare">
          <div class="overlay-inner">
            <img class="overlay-img" src={newSrc() ?? ''} alt="new" />
            <img class="overlay-img new" src={oldSrc() ?? ''} alt="old" />
          </div>
        </div>
      </Show>
    </Show>
  );
}

function DiffView(props: { old: CaptureRow | null; new: CaptureRow | null }) {
  return (
    <div class="diff-compare">
      <Show
        when={props.old && props.new}
        fallback={<div class="no-image">Diff impossible : captures manquantes</div>}
      >
        <div class="diff-legend">
          <span class="added">+ added</span>
          <span class="removed">− removed</span>
          <span class="changed">⚠ changed</span>
        </div>
        <div class="no-image">
          Diff endpoint (Phase 10E) — GET /api/v1/captures/{props.new?.id}/diff/{props.old?.variantId}/{props.new?.variantId}
        </div>
      </Show>
    </div>
  );
}
